import * as R from 'ramda';
import t from 'tcomb-form';
import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import ErrorBubble from 'js/components/error_bubble';
import Linkify from 'react-linkify';

import {prettyDatetimeNoYear} from 'js/common/date_helper';
import proxyRequest from 'js/proxy_api_client';
import logger from 'js/common/logger';

import ManualDistributionPanel from './manual_distribution_panel';
import LoadingSpinner from '../loading_spinner/index';

const RealNumber = t.maybe(
    t.refinement(t.Number, val => {
        if (val) {
            return val >= 0;
        }

        return true;
    })
);

const fields = {
    url: t.maybe(
        t.refinement(t.String, val => {
            return val.length <= 256;
        })
    ),
    caption: t.maybe(t.String),
    notes: t.maybe(t.String),

    impressions: RealNumber,
    reach: RealNumber,
    likes: RealNumber,
    dislikes: RealNumber,
    favorites: RealNumber,
    replies: RealNumber,
    shares: RealNumber,
    views: RealNumber,
    followers: RealNumber
};

const FormOptions = {
    fields: {
        url: {
            label: 'URL (optional but recommended)',
            error: 'Please enter a valid url no greater than 256 characters.'
        },
        caption: {
            type: 'textarea',
            label: 'Caption (optional)'
        },
        followers: {
            label: 'Follower Count (optional)',
            error: 'Must be numeric.'
        },
        impressions: {
            label: 'Impressions (optional)',
            error: 'Must be numeric.'
        },
        reach: {
            label: 'Reach (optional)',
            error: 'Must be numeric.'
        },
        likes: {
            label: 'Likes (optional)',
            error: 'Must be numeric.'
        },
        dislikes: {
            label: 'Dislikes (optional)',
            error: 'Must be numeric.'
        },
        favorites: {
            label: 'Favorites (optional)',
            error: 'Must be numeric.'
        },
        replies: {
            label: 'Comments/Replies (optional)',
            error: 'Must be numeric.'
        },
        shares: {
            label: 'Shares (optional)',
            error: 'Must be numeric.'
        },
        views: {
            label: 'Views (optional)',
            error: 'Must be numeric.'
        },
        notes: {
            type: 'textarea',
            label: 'Notes (optional, not visible to creators)'
        }
    }
};

@inject(
    ({
        contentMetadataId,
        socialUserId,
        distribution,
        setView,
        campaignMetadataStore,
        postStateMachineStore
    }) => ({
        contentMetadataId,
        socialUserId,
        distribution,
        setView,
        campaignMetadata: campaignMetadataStore.campaignMetadata,
        ...postStateMachineStore
    })
)
@observer
export default class ManualInfeedDistributionContainer extends Component {
    static defaultProps = {
        distribution: {}
    };

    constructor(props) {
        super(props);

        this.state = {
            value:
                props.distribution && props.distribution.id
                    ? this.transformDistributionToFormState(props.distribution)
                    : null,
            error: null,

            fetchedMetrics: false,
            transmitting: false,
            showConfirmation: false
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.distribution.id && this.props.distribution.id) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                value: this.transformDistributionToFormState(this.props.distribution)
            });
        }
    }

    transformDistributionToFormState = distribution => {
        const newState = {};

        if (!distribution) {
            return newState;
        }

        if (!distribution.distribution_metadata) {
            return newState;
        }

        const {
            distribution_metadata: {
                caption,
                distribution_platform_post_url,
                extra,
                snapshots: {social_engagements , platform_account}
            }
        } = distribution;

        if (caption) {
            newState.caption = caption;
        }

        if (distribution_platform_post_url) {
            newState.url = distribution_platform_post_url;
        }

        if (extra && extra.notes) {
            newState.notes = extra.notes;
        }

        if (platform_account.follower_count) {
            newState.followers = platform_account.follower_count;
        }

        if (!social_engagements) {
            return newState;
        }

        const {
            impressions,
            reach,
            likes,
            replies,
            views,
            dislikes,
            favorites,
            shares
        } = social_engagements;

        if (impressions) {
            newState.impressions = impressions;
        }

        if (reach) {
            newState.reach = reach;
        }

        if (likes) {
            newState.likes = likes;
        }

        if (replies) {
            newState.replies = replies;
        }

        if (views) {
            newState.views = views;
        }

        if (dislikes) {
            newState.dislikes = dislikes;
        }

        if (favorites) {
            newState.favorites = favorites;
        }

        if (shares) {
            newState.shares = shares;
        }

        return newState;
    };

    getSchema = () => {
        const {campaignMetadata} = this.props;

        if (campaignMetadata.type === 'instagram' || campaignMetadata.type === 'tiktok') {
            return R.pick(
                ['url', 'caption', 'followers', 'impressions', 'reach', 'likes', 'replies', 'shares', 'views', 'notes'],
                fields
            );
        }

        if (campaignMetadata.type === 'youtube') {
            return R.pick(['url', 'likes', 'dislikes', 'replies', 'views', 'notes'], fields);
        }

        if (campaignMetadata.type === 'twitter') {
            return R.pick(['url', 'favorites', 'replies', 'shares', 'views', 'notes'], fields);
        }

        if (campaignMetadata.type === 'snapchat') {
            return R.pick(['views', 'notes'], fields);
        }
    };

    handleSubmit = async event => {
        event.preventDefault();

        if (!this.form) {
            return;
        }

        const validation = this.form.validate();

        if (validation.errors.length) {
            this.setState({
                error: 'Some inputs are invalid.',
                value: validation.value
            });
        } else {
            this.setState({
                error: null,
                value: this.form.getValue(),
                showConfirmation: true
            });
        }
    };

    handleConfirm = async e => {
        e.preventDefault();
        try {
            await this.setStateP({transmitting: true});

            const {data} = await proxyRequest('post', 'api/v1/manual_distribution/upsert_infeed', {
                data: {
                    content_metadata_id: this.props.contentMetadataId,
                    distribution: this.state.value
                }
            });

            if (data.user_error) {
                this.setState({
                    transmitting: false,
                    error: data.user_error.message
                });

                return;
            }

            await this.setStateP({upsertedDistribution: true});

            setTimeout(async () => {
                await this.setStateP({
                    error: null,
                    transmitting: false,
                    fetchedMetrics: false,
                    value: {}
                });

                this.props.onCloseModal();
            }, 1000);
        } catch (error) {
            this.setState({
                transmitting: false,
                error: 'Something went wrong while submitting your changes. Please try again.'
            });

            logger.error(error.message, {error});
        }
    };

    handleDontConfirm = e => {
        e.preventDefault();

        this.setState({showConfirmation: false});
    };

    handleCancel = async e => {
        await this.resetState();

        e.preventDefault();
        this.props.onCloseModal();

        // // only go back to story manual distribution type view when it's Instagram
        // if (this.props.campaignMetadata.type !== 'instagram') {
        //     e.preventDefault();
        //     this.props.onCloseModal();
        // } else {
        //     this.props.setView(e, null);
        // }
    };

    resetState = () => {
        return this.setStateP({
            error: null,
            transmitting: false,
            fetchedMetrics: false,
            value: this.props.distribution.id
                ? this.transformDistributionToFormState(this.props.distribution)
                : null
        });
    };

    fetchMetrics = async e => {
        e.preventDefault();

        if (this.state.transmitting) {
            return;
        }

        const {
            socialUserId,

            campaignMetadata: {type: platform}
        } = this.props;

        await this.syncFormStateToComponentState();

        const {url} = this.state.value;

        if (!url || !url.length) {
            this.setState({error: 'Input a post url to get metrics.'});
            return;
        }

        if (!socialUserId || !socialUserId.length) {
            this.setState({
                error:
                    `We are missing the creator's id on ${platform}` +
                    ' and cannot get metrics for this post.'
            });

            return;
        }

        await this.setStateP({fetchedMetrics: false, transmitting: true});

        try {
            const {data} = await proxyRequest('post', 'api/v1/manual_distribution/fetch_metrics', {
                data: {
                    social_user_id: socialUserId.toString(),
                    platform,
                    url
                }
            });

            if (data.user_error) {
                this.setState({transmitting: false, error: data.user_error.message});
                return;
            }

            const value = R.pipe(
                /**
                 * Only get values relevant to the schema we are trying to fill in.
                 */
                R.pick(R.keys(this.getSchema())),
                R.merge(this.state.value)
            )(data);

            this.setState({
                fetchedMetrics: true,
                transmitting: false,
                value
            });
        } catch (error) {
            this.setState({
                error: 'Failed to get metrics on this post.',
                transmitting: false
            });
        }
    };

    /** Promisify setState */
    setStateP = newState => {
        return new Promise(resolve => this.setState(newState, resolve));
    };

    syncFormStateToComponentState = async e => {
        if (e) {
            e.preventDefault();
        }

        await this.setStateP({value: this.form.getValue()});
    };

    renderPreviousDistributionInfo = () => {
        const {distribution} = this.props;

        if (!distribution || !distribution.id) {
            return null;
        }

        const {distribution_metadata} = distribution;

        const info = [
            <span key="updating-dist-id">
                Updating <b>&#35;{distribution.id}</b>
            </span>
        ];

        if (
            distribution_metadata.extra &&
            distribution_metadata.extra.audit &&
            distribution_metadata.extra.audit.length
        ) {
            const {email, timestamp} = R.last(distribution_metadata.extra.audit);

            info.push(
                ' ',
                <span key="created-by-email">
                    created by <b>{email}</b> on <b>{prettyDatetimeNoYear(timestamp)}</b>
                </span>
            );
        }

        return (
            <div className="form-group">
                <div className="form-label info-bubble">{info}</div>
            </div>
        );
    };

    renderActions = () => {
        if (this.state.showConfirmation) {
            return (
                <div style={{display: 'grid'}}>
                    <div className="btn-group btn-group-vertical">
                        <button
                            disabled={this.state.transmitting}
                            className="btn btn-danger"
                            onClick={this.handleConfirm}
                        >
                            I&apos;m Sure
                        </button>
                        <button
                            disabled={this.state.transmitting}
                            onClick={this.handleDontConfirm}
                            className="btn btn-default"
                        >
                            Nevermind
                        </button>
                    </div>

                    {this.state.error ? <ErrorBubble message={this.state.error} /> : null}
                </div>
            );
        }

        return (
            <div style={{display: 'grid'}}>
                <div className="btn-group btn-group-vertical">
                    {(this.props.campaignMetadata.type === 'instagram'
                    || this.props.campaignMetadata.type === 'tiktok') ? (
                        <OverlayTrigger
                            overlay={
                                <Tooltip>
                                    Attempt to fetch up-to-date metrics from the social platform.
                                </Tooltip>
                            }
                        >
                            <button
                                disabled={this.state.transmitting}
                                className="btn btn-warning"
                                onClick={this.fetchMetrics}
                            >
                                Fetch {this.state.fetchedMetrics ? '🗸' : null}
                            </button>
                        </OverlayTrigger>
                    ) : null}
                    <button
                        disabled={this.state.transmitting}
                        onClick={this.handleSubmit}
                        className="btn btn-primary"
                    >
                        Save
                    </button>
                    <button
                        disabled={this.state.transmitting}
                        onClick={this.handleCancel}
                        className="btn btn-default"
                    >
                        Cancel
                    </button>
                </div>

                {this.state.error ? <ErrorBubble message={this.state.error} /> : null}
            </div>
        );
    };

    render() {
        if (this.state.showConfirmation) {
            return (
                <ManualDistributionPanel actions={this.renderActions()}>
                    <div className="manual-distribution__editor">
                        <div className="manual-distribution__editor-header">
                            <h2>
                                {this.props.distribution.id ? 'Update' : 'Create'} this
                                distribution?
                            </h2>
                            {this.state.upsertedDistribution ? (
                                <small className="success-bubble">Success!</small>
                            ) : (
                                <small className="info-bubble">
                                    Distributions <b>created or updated</b> here will not be
                                    refreshed by the platform. Furthermore, the posts will be
                                    fast-tracked to an approved state.
                                </small>
                            )}
                        </div>

                        <div className="manual-distribution__editor-body">
                            <table className="manual-distribution__editor-metrics-table">
                                <tbody>
                                    {Object.keys(this.state.value).map(key => {
                                        return (
                                            <tr>
                                                <td key={`td-${key}`}>{key}</td>
                                                <td key={`td-${key}-val`}>
                                                    <div className="metrics-table__value">
                                                        <Linkify
                                                            key={key}
                                                            properties={{target: '_blank'}}
                                                        >
                                                            {this.state.value[key] || '(empty)'}
                                                        </Linkify>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </ManualDistributionPanel>
            );
        }

        return (
            <ManualDistributionPanel actions={this.renderActions()}>
                <div className="manual-distribution__editor">
                    <div className="manual-distribution__editor-header">
                        <h2>
                            In-Feed Metrics {this.state.transmitting ? <LoadingSpinner /> : null}
                        </h2>
                    </div>

                    <div className="manual-distribution__editor-body">
                        <form onSubmit={this.handleSubmit}>
                            {this.renderPreviousDistributionInfo()}

                            <t.form.Form
                                ref={component => {
                                    this.form = component;
                                }}
                                options={FormOptions}
                                type={t.struct(this.getSchema())}
                                value={this.state.value}
                            />
                        </form>
                    </div>
                </div>
            </ManualDistributionPanel>
        );
    }
}



// WEBPACK FOOTER //
// ./components/manual_distribution_modal/manual_infeed_distribution_container.js